// OddsLib from https://github.com/1player/oddslib
// This lib has been inlined into the app codebase due to its criticality and
// the small size of the source library.

// MIT License

// Copyright (c) 2017 Stéphane Travostino <steph@combo.cc>

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

// --
// Approximate Fraction
// From http://www.mindspring.com/~alanh/fracs.html

// Approximate decimal number `d` into the returned fraction `frac`,
// so that |frac - d| < (10^-precision / 2).
// In other words, return an approximate fraction correct up to the
// `precision`th decimal place.

function approximateFraction(d, precision) {
  var numerators = [0, 1];
  var denominators = [1, 0];

  var maxNumerator = getMaxNumerator(d);
  var d2 = d;
  var calcD,
    prevCalcD = NaN;

  var acceptableError = Math.pow(10, -precision) / 2;

  for (var i = 2; i < 1000; i++) {
    var L2 = Math.floor(d2);
    numerators[i] = L2 * numerators[i - 1] + numerators[i - 2];
    if (Math.abs(numerators[i]) > maxNumerator) return;

    denominators[i] = L2 * denominators[i - 1] + denominators[i - 2];

    calcD = numerators[i] / denominators[i];

    if (Math.abs(calcD - d) < acceptableError || calcD === prevCalcD) {
      return numerators[i].toString() + "/" + denominators[i].toString();
    }

    d2 = 1 / (d2 - L2);
  }
}

function getMaxNumerator(f) {
  var f2 = null;
  var ixe = f.toString().indexOf("E");
  if (ixe === -1) ixe = f.toString().indexOf("e");
  if (ixe === -1) f2 = f.toString();
  else f2 = f.toString().substring(0, ixe);

  var digits = null;
  var ix = f2.toString().indexOf(".");
  if (ix === -1) digits = f2;
  else if (ix === 0) digits = f2.substring(1, f2.length);
  else if (ix < f2.length)
    digits = f2.substring(0, ix) + f2.substring(ix + 1, f2.length);

  var L = digits;

  var numDigits = L.toString().length;
  var L2 = f;
  var numIntDigits = L2.toString().length;
  if (L2 === 0) numIntDigits = 0;
  var numDigitsPastDecimal = numDigits - numIntDigits;

  var i;
  for (i = numDigitsPastDecimal; i > 0 && L % 2 === 0; i--) L /= 2;
  for (i = numDigitsPastDecimal; i > 0 && L % 5 === 0; i--) L /= 5;

  return L;
}

// --
// Actual Odds Lib

// 1.2 - 1.0 === 0.19999999999999996
// fixFloatError(1.2 - 1.0) === 0.2
var fixFloatError = function (n) {
  return parseFloat(n.toPrecision(12));
};

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round
function decimalAdjust(type, value, exp) {
  // If the exp is undefined or zero...
  if (typeof exp === "undefined" || +exp === 0) {
    return Math[type](value);
  }
  value = +value;
  exp = +exp;
  // If the value is not a number or the exp is not an integer...
  if (isNaN(value) || !(typeof exp === "number" && exp % 1 === 0)) {
    return NaN;
  }
  // If the value is negative...
  if (value < 0) {
    return -decimalAdjust(type, -value, exp);
  }
  // Shift
  value = value.toString().split("e");
  value = Math[type](+(value[0] + "e" + (value[1] ? +value[1] - exp : -exp)));
  // Shift back
  value = value.toString().split("e");
  return +(value[0] + "e" + (value[1] ? +value[1] + exp : exp));
}

var FORMATS = {
  // European/Decimal format
  decimal: {
    from: function (decimal) {
      decimal = parseFloat(decimal);
      return decimal;
    },
    to: function () {
      return this.decimalValue;
    },
  },

  // American/Moneyline format
  moneyline: {
    from: function (moneyline) {
      moneyline = parseFloat(moneyline);

      if (moneyline >= 0) {
        return moneyline / 100.0 + 1;
      }
      return 100 / -moneyline + 1;
    },
    to: function () {
      if (this.decimalValue >= 2) {
        return fixFloatError((this.decimalValue - 1) * 100.0);
      }
      return fixFloatError(-100 / (this.decimalValue - 1));
    },
  },

  // Hong Kong format
  hongKong: {
    from: function (hongKong) {
      hongKong = parseFloat(hongKong);
      if (hongKong < 0.0) {
        throw new Error("Outside valid range.");
      }
      return hongKong + 1.0;
    },
    to: function () {
      return fixFloatError(this.decimalValue - 1);
    },
  },

  // Implied probability
  impliedProbability: {
    from: function (ip) {
      // Handle percentage string
      if (typeof ip === "string" && ip.slice(-1) === "%") {
        ip = parseFloat(ip) / 100.0;
      } else {
        ip = parseFloat(ip);
      }

      if (ip <= 0.0 || ip >= 1.0) {
        throw new Error("Outside valid range");
      }

      return 1.0 / ip;
    },
    to: function (options) {
      if (options.percentage) {
        var value = fixFloatError(100.0 / this.decimalValue);

        // HACK: Oddslib.prototype.to calls decimalAdjust if we return a number.
        // But we need to round before adding the % symbol.
        // So we do it here and return the string
        if (options.precision !== null) {
          value = decimalAdjust("round", value, -options.precision);
        }

        return value.toString() + "%";
      }

      return fixFloatError(1 / this.decimalValue);
    },
  },

  // UK/Fractional format
  fractional: {
    from: function (n) {
      // Try to split on the slash
      var pieces = n.toString().split("/");

      n = parseFloat(pieces[0]);

      var d;
      if (pieces.length === 2) {
        d = parseFloat(pieces[1]);
      } else if (pieces.length === 1) {
        d = 1;
      } else {
        throw new Error("Invalid fraction");
      }

      if (n === 0 || d === 0 || n / d <= 0.0) {
        throw new Error("Outside valid range");
      }

      return 1 + n / d;
    },
    to: function (options) {
      return approximateFraction(
        this.decimalValue - 1,
        options.precision || 12,
      );
    },
  },

  // Malay format
  malay: {
    from: function (malay) {
      malay = parseFloat(malay);

      if (malay <= -1.0 || malay > 1.0) {
        throw new Error("Outside valid range.");
      }

      if (malay < 0) {
        malay = -1 / malay;
      }
      return malay + 1;
    },
    to: function () {
      if (this.decimalValue <= 2.0) {
        return fixFloatError(this.decimalValue - 1);
      }
      return fixFloatError(-1 / (this.decimalValue - 1));
    },
  },

  // Indonesian format
  indonesian: {
    from: function (indonesian) {
      indonesian = parseFloat(indonesian);

      if (indonesian === 0) {
        throw new Error("Outside valid range.");
      }

      if (indonesian >= 1) {
        return indonesian + 1;
      }
      return -1 / indonesian + 1;
    },
    to: function () {
      if (this.decimalValue < 2.0) {
        return fixFloatError(-1 / (this.decimalValue - 1));
      }
      return fixFloatError(this.decimalValue - 1);
    },
  },
};

var Odds = (function () {
  // Private constructor pattern
  // from http://stackoverflow.com/a/21731713
  var PublicOdds = function () {
    throw new Error(
      "This constructor is private, please use the from* functions",
    );
  };
  var Odds = function (decimalValue) {
    if (typeof decimalValue !== "number" || isNaN(decimalValue)) {
      throw new Error("Invalid odds");
    }

    this.decimalValue = fixFloatError(decimalValue);
  };
  Odds.prototype = PublicOdds.prototype;

  // Generic constructor
  PublicOdds.from = function (format, value) {
    if (!Object.hasOwn(FORMATS, format)) {
      throw new Error("Unknown format " + format + ".");
    }
    var decimal = FORMATS[format].from(value);
    return new Odds(decimal);
  };

  return PublicOdds;
})();

// Conversion API
Odds.prototype.to = function (format, options) {
  if (!Object.hasOwn(FORMATS, format)) {
    throw new Error("Unknown format " + format + ".");
  }

  options = Object.assign(
    {
      precision: null,
      percentage: false,
    },
    options,
  );

  var ret = FORMATS[format].to.call(this, options);
  if (typeof ret === "number" && options.precision !== null) {
    ret = decimalAdjust("round", ret, -options.precision);
  }
  return ret;
};

module.exports = {
  Odds: Odds,

  from: Odds.from,
};
